/**
 * BottomSheet — production overlay sheet / slide-over (design revamp,
 * handoff-2026-06).
 *
 * A modal surface that slides up from the bottom on mobile and in from
 * the right on desktop (`desktop`). It dims the page with a scrim
 * (click-to-close), traps focus, closes on Escape, and restores focus to
 * the previously-focused element on close. Animation is Framer Motion,
 * transform/opacity only, eased with `var(--g-ease)`; under
 * `prefers-reduced-motion` the slide is dropped (the panel just
 * fades/appears) per the reduced-motion contract.
 *
 * Affordances:
 *   • a drag-handle bar at the top on mobile (decorative grip)
 *   • an X close button when `title` is provided (alongside the title)
 *
 * Accessibility: `role="dialog"` + `aria-modal="true"`; when `title` is
 * set it labels the dialog via `aria-labelledby`. Escape closes. Focus
 * moves into the panel on open and is restored to the opener on close.
 *
 * Token mapping (all via theme.css `--g-*`):
 *   scrim   -> bg-[var(--g-scrim)]
 *   panel   -> bg-[var(--g-elevated)] + shadow-[var(--g-shadow-xl)]
 *   radius  -> rounded-[var(--g-radius-lg)] (top corners mobile / left desktop)
 *   handle  -> bg-[var(--g-hairline)]
 *
 * Visual reference: docs/GardEn/handoff-2026-06/garden/ui.jsx
 * `BottomSheet` + docs/GardEn/handoff-2026-06/garden/screens-shops.jsx /
 * screens-chat.jsx usage.
 */
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  type Transition,
} from "framer-motion";
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactElement,
  type ReactNode,
} from "react";

import { Icon } from "./Icon";

export interface BottomSheetProps {
  /** Whether the sheet is open. */
  open: boolean;
  /** Called when the sheet requests close (scrim click, X, or Escape). */
  onClose: () => void;
  /** Optional title; when set, renders the title row + an X close button. */
  title?: ReactNode;
  /** Desktop variant — slides in from the right as a side panel. */
  desktop?: boolean;
  /** Sheet content. */
  children: ReactNode;
}

/** Eased, transform/opacity-only transition (per the motion contract). */
const SHEET_TRANSITION: Transition = {
  duration: 0.3,
  ease: [0.2, 0.7, 0.3, 1], // mirrors --g-ease: cubic-bezier(.2,.7,.3,1)
};

/** Selector for focusable descendants (used by the focus trap). */
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

/**
 * Renders the modal sheet via `AnimatePresence` (so the exit animation
 * runs before unmount). Mounts nothing when closed and idle.
 */
export function BottomSheet({
  open,
  onClose,
  title,
  desktop = false,
  children,
}: BottomSheetProps): ReactElement {
  const reduceMotion = useReducedMotion();
  const panelRef = useRef<HTMLDivElement | null>(null);
  // The element focused before the sheet opened, restored on close.
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const reactId = useId();
  const titleId = `garden-bottom-sheet-title-${reactId.replace(/:/g, "")}`;

  // Capture the opener and move focus into the panel when opening; restore
  // focus to the opener when closing.
  useEffect(() => {
    if (open) {
      restoreFocusRef.current =
        (document.activeElement as HTMLElement | null) ?? null;
      // Defer until the panel is in the DOM.
      const raf = requestAnimationFrame(() => {
        const panel = panelRef.current;
        if (!panel) return;
        const first = panel.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
        (first ?? panel).focus();
      });
      return () => cancelAnimationFrame(raf);
    }
    // On close, restore focus to the opener (if it is still in the DOM).
    const toRestore = restoreFocusRef.current;
    if (toRestore && document.contains(toRestore)) {
      toRestore.focus();
    }
    restoreFocusRef.current = null;
    return undefined;
  }, [open]);

  // Escape-to-close, bound at the document level while open so it fires
  // regardless of where focus currently sits (the synthetic-event path
  // only sees keys when focus is already inside the sheet).
  useEffect(() => {
    if (!open) return undefined;
    const onDocumentKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", onDocumentKeyDown);
    return () => document.removeEventListener("keydown", onDocumentKeyDown);
  }, [open, onClose]);

  // A simple focus trap (Tab cycles within the panel) on the container.
  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (event.key !== "Tab") return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusables = Array.from(
        panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      );
      if (focusables.length === 0) {
        // Nothing tabbable: keep focus on the panel.
        event.preventDefault();
        panel.focus();
        return;
      }
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement;
      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    },
    // Only references module-level constants + refs, so no reactive deps.
    [],
  );

  // Hidden (off-screen) state for the slide. Under reduced motion we drop
  // the translate entirely (opacity-only), per the motion contract.
  const hidden = reduceMotion
    ? { opacity: 0 }
    : desktop
      ? { opacity: 1, x: "100%", y: 0 }
      : { opacity: 1, x: 0, y: "100%" };
  const shown = reduceMotion
    ? { opacity: 1 }
    : { opacity: 1, x: 0, y: 0 };

  const panelClasses = [
    "relative",
    "flex",
    "max-h-full",
    "flex-col",
    "overflow-y-auto",
    "bg-[var(--g-elevated)]",
    "p-5",
    "text-foreground",
    "shadow-[var(--g-shadow-xl)]",
    "focus:outline-none",
    desktop
      ? "h-full w-full max-w-[420px] rounded-l-[var(--g-radius-lg)]"
      : "w-full max-w-[520px] rounded-t-[var(--g-radius-lg)]",
  ].join(" ");

  const containerAlign = desktop
    ? "items-stretch justify-end"
    : "items-end justify-center";

  return (
    <AnimatePresence>
      {open ? (
        <div
          data-testid="g-bottom-sheet"
          data-desktop={desktop ? "true" : "false"}
          className={`fixed inset-0 z-[60] flex ${containerAlign}`}
          onKeyDown={onKeyDown}
        >
          {/* Scrim — click closes. Decorative; the dialog owns semantics. */}
          <motion.div
            data-testid="g-bottom-sheet-scrim"
            aria-hidden="true"
            className="absolute inset-0 bg-[var(--g-scrim)]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={SHEET_TRANSITION}
            onClick={onClose}
          />

          {/* Panel — the modal dialog surface. */}
          <motion.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={title != null ? titleId : undefined}
            tabIndex={-1}
            className={panelClasses}
            initial={hidden}
            animate={shown}
            exit={hidden}
            transition={SHEET_TRANSITION}
          >
            {/* Drag-handle grip — mobile only, decorative. */}
            {!desktop ? (
              <div
                data-testid="g-bottom-sheet-handle"
                aria-hidden="true"
                className="mx-auto mb-3.5 h-1 w-[38px] rounded-full bg-[var(--g-hairline)]"
              />
            ) : null}

            {/* Title row + X close button, when a title is supplied. */}
            {title != null ? (
              <div className="mb-3.5 flex items-center justify-between">
                <div
                  id={titleId}
                  className="text-[17px] font-bold tracking-[-0.02em]"
                >
                  {title}
                </div>
                <button
                  type="button"
                  data-testid="g-bottom-sheet-close"
                  onClick={onClose}
                  className="grid h-8 w-8 place-items-center rounded-full bg-secondary text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                  aria-label="Close"
                >
                  <Icon name="x" size={17} />
                </button>
              </div>
            ) : null}

            {children}
          </motion.div>
        </div>
      ) : null}
    </AnimatePresence>
  );
}
